import React, {
  useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { settingGet, settingPost } from '../../../utils/settingApi';
import {
  accionesPara, etiquetaAccion, etiquetaEtapa, TEXTO_PENDIENTE, fechaHora, linkInstagram, textoEvento, mananaChile,
} from '../../../utils/settingFormato';

// Ficha del lead (spec §5.3): lo que sabemos, línea de tiempo, notas, acciones y "Abrir en Instagram". Un cambio
// queda en la cola del Sheet y se ve como pendiente hasta que la Bandeja lo aplica (hasta 5 minutos).
// Es un diálogo modal: al abrir, el foco entra al botón Cerrar y la página de atrás no se mueve; Escape la cierra y
// el foco vuelve a la tarjeta que la abrió (`origen`). El origen llega desde la página porque Safari no le da el foco
// a un botón al hacerle clic, así que `document.activeElement` no sirve para saber de dónde vino.
const SABEMOS = [['dolor', 'Dolor'], ['objetivo', 'Objetivo'], ['meta', 'Meta'], ['por_que_ahora', 'Por qué ahora']];

// Una persona de campaña (conv_id "campana:…") todavía no tiene chat: la Bandeja solo le acepta "No califica" (y la
// nota). Las demás acciones volverían rechazadas, así que ni se ofrecen.
const esCampana = (convId) => String(convId).startsWith('campana:');
const TEXTO_CAMPANA = 'Todavía no tiene chat con AU: Julio le escribe desde Instagram.';

const textoPendiente = (c) => `${etiquetaAccion(c.accion)}${c.valor && c.accion !== 'nota' ? ` (${c.valor})` : ''}: ${TEXTO_PENDIENTE}`;

const FichaLead = ({
  convId, pendientes, onCambio, onCerrar, origen = null, onSinSesion = () => {},
}) => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [fecha, setFecha] = useState('');
  const [nota, setNota] = useState('');
  const [enviando, setEnviando] = useState(false);
  const cerrarRef = useRef(null);
  const onCerrarRef = useRef(onCerrar);
  onCerrarRef.current = onCerrar;
  const origenRef = useRef(origen);
  const onSinSesionRef = useRef(onSinSesion);
  onSinSesionRef.current = onSinSesion;

  // Layout y no passive: al desmontar (Escape, Cerrar o la sesión vencida que manda al login) el scroll y el foco se
  // devuelven en el mismo commit, sin un cuadro con la página todavía bloqueada.
  useLayoutEffect(() => {
    const volverA = origenRef.current;
    const overflowAntes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (cerrarRef.current) cerrarRef.current.focus();
    const tecla = (e) => { if (e.key === 'Escape') onCerrarRef.current(); };
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('keydown', tecla);
      document.body.style.overflow = overflowAntes;
      if (volverA && volverA.isConnected) volverA.focus();
    };
  }, []);

  useEffect(() => {
    let vivo = true;
    setDatos(null);
    setError('');
    settingGet(`leads/${encodeURIComponent(convId)}`)
      .then((d) => { if (vivo) setDatos(d); })
      .catch((e) => {
        if (!vivo) return;
        // Sesión vencida: a la página le toca mandar al login, no a la ficha decir "No autorizado".
        if (e?.response?.status === 401) onSinSesionRef.current();
        else setError(e?.response?.data?.error || 'No pude abrir la ficha.');
      });
    return () => { vivo = false; };
  }, [convId]);

  const pedir = async (accion, valor = '') => {
    if (enviando) return;
    setEnviando(true);
    setError('');
    try {
      const r = await settingPost('cambios', { conv_id: convId, accion, valor });
      onCambio(r.cambio);
      if (accion === 'nota') setNota('');
    } catch (e) {
      if (e?.response?.status === 401) {
        onSinSesion();
        return;
      }
      setError(e?.response?.data?.error || 'No se pudo guardar el cambio. Intenta de nuevo.');
    }
    setEnviando(false);
  };

  const lead = datos?.lead;
  const campana = esCampana(convId);
  const notas = (datos?.cambios || []).filter((c) => c.accion === 'nota');
  return (
    <div className="au-admin au-set-fondo" role="presentation" onClick={onCerrar}>
      <aside className="au-set-ficha" role="dialog" aria-modal="true" aria-label="Ficha del lead" onClick={(e) => e.stopPropagation()}>
        <div className="au-set-ficha-cabeza">
          <h2>{lead ? `@${lead.usuario}` : 'Ficha'}</h2>
          <button ref={cerrarRef} type="button" className="au-set-btn" onClick={onCerrar} aria-label="Cerrar la ficha">Cerrar</button>
        </div>
        {error && <p className="au-set-error" role="alert">{error}</p>}
        {!datos && !error && <p className="mono muted">Cargando…</p>}
        {lead && (
          <>
            <p className="mono">
              {`${etiquetaEtapa(lead.etapa)} · paso ${lead.paso} · ${lead.dueno === 'agustin' ? 'lo lleva Agustín' : 'lo lleva Julio'}`}
            </p>
            <a className="au-set-ig" href={linkInstagram(lead.usuario)} target="_blank" rel="noreferrer">Abrir en Instagram</a>
            {campana && <p className="mono muted">{TEXTO_CAMPANA}</p>}
            {pendientes.length > 0 && (
              <ul className="au-set-pendientes">{pendientes.map((c) => <li key={c.id}>{textoPendiente(c)}</li>)}</ul>
            )}
            <section>
              <h3 className="au-set-titulo">Lo que sabemos</h3>
              <dl className="au-set-sabemos">
                {SABEMOS.map(([clave, etiqueta]) => (
                  <React.Fragment key={clave}>
                    <dt>{etiqueta}</dt>
                    <dd>{lead[clave] || 'falta'}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </section>
            <section>
              <h3 className="au-set-titulo">Acciones</h3>
              <div className="au-set-acciones">
                {accionesPara(lead).filter((a) => (campana ? a.accion === 'no_califica' : a.accion !== 'mas_adelante')).map((a) => (
                  <button key={a.accion} type="button" className="au-set-btn" disabled={enviando} onClick={() => pedir(a.accion)}>
                    {a.etiqueta}
                  </button>
                ))}
              </div>
              {!campana && lead.etapa !== 'mas_adelante' && (
                <div className="au-set-acciones">
                  <input
                    type="date"
                    aria-label="Fecha para retomar"
                    min={mananaChile()}
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                  />
                  <button type="button" className="au-set-btn" disabled={enviando} onClick={() => pedir('mas_adelante', fecha)}>
                    Más adelante
                  </button>
                </div>
              )}
              <div className="au-set-nota">
                <textarea
                  aria-label="Nota"
                  maxLength={1000}
                  value={nota}
                  placeholder="Nota para Julio y Agustín"
                  onChange={(e) => setNota(e.target.value)}
                />
                <button type="button" className="au-set-btn" disabled={enviando || !nota.trim()} onClick={() => pedir('nota', nota.trim())}>
                  Guardar nota
                </button>
              </div>
            </section>
            {notas.length > 0 && (
              <section>
                <h3 className="au-set-titulo">Notas</h3>
                <ul className="au-set-linea">
                  {notas.map((c) => <li key={c.id}>{`${fechaHora(c.fecha)} · ${c.valor} (${c.quien})`}</li>)}
                </ul>
              </section>
            )}
            <section>
              <h3 className="au-set-titulo">Línea de tiempo</h3>
              <ul className="au-set-linea">
                {(datos.eventos || []).map((e) => (
                  <li key={e.id}><span className="mono muted">{fechaHora(e.fecha)}</span> {textoEvento(e)}</li>
                ))}
              </ul>
            </section>
          </>
        )}
      </aside>
    </div>
  );
};

export default FichaLead;
